import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Upload, Download, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, formatApiError, API_BASE, tokenStorage } from "../lib/api";
import { toast } from "sonner";

export default function ImportQuestionsCsvDialog({ open, onOpenChange, courseId, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setUploading(false);
    setResult(null);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/courses/${courseId}/questions/import-template`, {
        headers: { Authorization: `Bearer ${tokenStorage.get()}` },
      });
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_import_domande.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Errore download template");
    }
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/courses/${courseId}/questions/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      onImported?.(data);
      if (data.created.length > 0) {
        toast.success(`${data.created.length} domand${data.created.length === 1 ? "a creata" : "e create"}`);
      }
      if (data.created.length === 0 && data.skipped.length > 0) {
        toast.warning("Nessuna domanda importata: controlla le righe saltate");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="import-questions-csv-dialog" className="max-w-2xl rounded-sm bg-white">
        <DialogHeader>
          <DialogTitle className="wordmark text-brand-blue">Importa domande da CSV</DialogTitle>
          <DialogDescription>
            Colonne richieste: <code>text, option_1, option_2, option_3, option_4, correct_option</code> (1–4). Opzionali: <code>explanation, tags</code> (separati da <code>|</code>). Delimitatore <code>,</code> o <code>;</code>.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <button
              data-testid="import-questions-template-btn"
              type="button"
              onClick={downloadTemplate}
              className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2.5 font-semibold transition-colors"
            >
              <Download size={16} /> Scarica template CSV
            </button>

            <div className="rounded-sm border-2 border-dashed border-brand-line bg-brand-bg p-6 text-center">
              <Upload size={28} className="mx-auto text-brand-blue mb-3" />
              <input
                data-testid="import-questions-file-input"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-sm"
              />
              {file && (
                <div className="text-sm text-brand-muted mt-2">
                  File selezionato: <strong>{file.name}</strong> ({Math.round(file.size / 1024)} KB)
                </div>
              )}
            </div>
          </div>
        )}

        {result && (
          <div data-testid="import-questions-result" className="space-y-4 max-h-[380px] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-4">
                <CheckCircle2 size={20} className="text-emerald-600 mb-1" />
                <div className="text-xs uppercase tracking-widest text-emerald-700">Create</div>
                <div className="text-2xl font-semibold text-emerald-800" data-testid="import-questions-created-count">
                  {result.created.length}
                </div>
              </div>
              <div className="rounded-sm border border-amber-200 bg-amber-50 p-4">
                <AlertTriangle size={20} className="text-amber-600 mb-1" />
                <div className="text-xs uppercase tracking-widest text-amber-700">Saltate</div>
                <div className="text-2xl font-semibold text-amber-800" data-testid="import-questions-skipped-count">
                  {result.skipped.length}
                </div>
              </div>
            </div>

            {result.created.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-brand-ink mb-2">Domande create</h4>
                <div className="border border-brand-line rounded-sm divide-y divide-brand-line">
                  {result.created.map((c) => (
                    <div key={c.id} className="p-3 text-sm text-brand-ink">
                      {c.text}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.skipped.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-brand-ink mb-2">Righe saltate</h4>
                <div className="border border-brand-line rounded-sm divide-y divide-brand-line">
                  {result.skipped.map((s, i) => (
                    <div key={i} className="p-3 text-sm">
                      <span className="text-[10px] uppercase tracking-widest text-brand-muted mr-2">
                        Riga {s.row}
                      </span>
                      <span className="text-brand-ink">{s.text || "—"}</span>
                      <div className="text-xs text-amber-700 mt-1">{s.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => handleClose(false)}
            className="px-4 py-2 text-sm text-brand-muted hover:text-brand-ink"
          >
            {result ? "Chiudi" : "Annulla"}
          </button>
          {!result && (
            <button
              data-testid="import-questions-upload-btn"
              type="button"
              onClick={upload}
              disabled={!file || uploading}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {uploading && <Loader2 size={14} className="animate-spin" />}
              Importa
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
